import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDemo } from '../contexts/DemoContext';
import { getJobById, updateJob, getAllUsers, getSignedTimesheets, deleteSignedTimesheet, updateJobPhotoCount } from '../firebase/db';
import { onSnapshot, query, collection, orderBy } from 'firebase/firestore';
import { db } from '../firebase/index';
import { COLLECTIONS } from '../firebase/schema';
import { generateDemoJobs } from '../demo/demoData';
import TimesheetForm from '../components/TimesheetForm';
import PhotoUpload from '../components/PhotoUpload';
import TimesheetPDF from '../components/TimesheetPDF';
import { useTimesheets } from '../hooks/useTimesheets';
import { 
  searchZohoCustomers, 
  transformZohoToJob, 
  isZohoConnected, 
  connectZohoAccount,
  getZohoEstimates,
  getZohoEstimateDetails,
  getZohoInvoices,
  getZohoInvoiceDetails
} from '../firebase/zoho-oauth';
import './JobDetail.css';

const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, isAdmin } = useAuth();
  const { demoMode } = useDemo();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workers, setWorkers] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showClosePrompt, setShowClosePrompt] = useState(false);
  const [lightbox, setLightbox] = useState({ isOpen: false, index: 0, url: '', caption: '' });
  const [lbScale, setLbScale] = useState(1);
  const [lbPan, setLbPan] = useState({ x: 0, y: 0 });
  const [lbDragging, setLbDragging] = useState(false);
  const [lbDragStart, setLbDragStart] = useState({ x: 0, y: 0 });
  const [jobSignedTs, setJobSignedTs] = useState(null);
  const [showSignedModal, setShowSignedModal] = useState(false);
  const { timesheets, addTimesheet } = useTimesheets(id);
  
  // Zoho resync state
  const [zohoSearch, setZohoSearch] = useState('');
  const [zohoCustomers, setZohoCustomers] = useState([]);
  const [showZohoResults, setShowZohoResults] = useState(false);
  const [zohoLoading, setZohoLoading] = useState(false);
  const [zohoConnected, setZohoConnected] = useState(false);
  const [zohoEstimates, setZohoEstimates] = useState([]);
  const [zohoInvoices, setZohoInvoices] = useState([]);
  const [showEstimates, setShowEstimates] = useState(false);
  const [showInvoices, setShowInvoices] = useState(false);
  const [estimatesLoading, setEstimatesLoading] = useState(false);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [zohoDocType, setZohoDocType] = useState('estimate'); // 'estimate' | 'invoice'

  useEffect(() => {
    const fetchJob = async () => {
      setLoading(true);
      if (demoMode) {
        const demoJobs = generateDemoJobs();
        const found = demoJobs.find((j) => j.id === id) || demoJobs[0];
        setJob(found);
      } else {
        const data = await getJobById(id);
        setJob(data);
      }
      setLoading(false);
    };
    fetchJob();
  }, [id, demoMode]);

  // Fetch signed timesheet for this job — REAL-TIME listener so admin sees signatures immediately
  useEffect(() => {
    if (!id || demoMode) {
      console.log('[JobDetail] Skipping signed timesheet listener — id:', id, 'demoMode:', demoMode);
      return;
    }
    console.log('[JobDetail] Setting up signed timesheet listener for job:', id);

    // Listen to ALL signed timesheets (same query as before) and filter client-side
    // This avoids any potential permission issues with where() clauses
    const q = query(
      collection(db, COLLECTIONS.signedTimesheets),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snap) => {
      console.log('[JobDetail] Signed timesheet snapshot received, docs:', snap.docs.length);
      const allDocs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const found = allDocs.find((st) => st.jobId === id);
      if (found) {
        console.log('[JobDetail] Found signed timesheet for job:', id, 'signature length:', found.signature?.length);
        setJobSignedTs(found);
      } else {
        console.log('[JobDetail] No signed timesheet found for job:', id);
        setJobSignedTs(null);
      }
    }, (err) => {
      console.error('[JobDetail] Signed timesheet listener error:', err);
      // Fallback: one-time fetch
      getSignedTimesheets().then((all) => {
        const found = all.find((st) => st.jobId === id);
        console.log('[JobDetail] Fallback fetch result:', found ? `found ${found.id}` : 'not found');
        setJobSignedTs(found || null);
      });
    });

    return () => {
      console.log('[JobDetail] Cleaning up signed timesheet listener');
      unsubscribe();
    };
  }, [id, demoMode]);

  useEffect(() => {
    if (isAdmin) {
      getAllUsers().then((users) => {
        const labeled = users.map(u => ({
          ...u,
          label: `${u.displayName || 'Unknown'} ${u.role ? '(' + u.role + ')' : ''}`,
        }));
        setWorkers(labeled);
      });
    }
  }, [isAdmin]);

  const handleUpdate = async () => {
    await updateJob(id, editForm);
    setJob({ ...job, ...editForm });
    setEditing(false);
  };

  const startEdit = () => {
    setEditForm({ ...job });
    setEditing(true);
  };

  const handleTimesheetSubmitted = () => {
    if (job?.status === 'completed') {
      console.log('[JobDetail] Timesheet submitted but job already completed — no prompt needed');
      return;
    }
    setShowClosePrompt(true);
  };

  const handleCloseJob = async () => {
    await updateJob(id, { status: 'completed', completedAt: new Date().toISOString() });
    setJob({ ...job, status: 'completed', completedAt: new Date().toISOString() });
    setShowClosePrompt(false);
  };

  const handleKeepJobOpen = () => {
    setShowClosePrompt(false);
  };

  const handleCloneJob = () => {
    navigate('/jobs/new', { state: { cloneJob: job } });
  };

  const handlePhotosSaved = (uploadedUrls) => {
    const currentPhotos = job.jobPhotos || [];
    const updatedPhotos = [...currentPhotos, ...uploadedUrls];
    updateJob(id, { jobPhotos: updatedPhotos });
    setJob({ ...job, jobPhotos: updatedPhotos });
    updateJobPhotoCount(id);
  };

  // ─── Lightbox Navigation ───
  const goToPhoto = useCallback((idx) => {
    if (!job?.jobPhotos?.length) return;
    const len = job.jobPhotos.length;
    const safeIdx = ((idx % len) + len) % len;
    const photo = job.jobPhotos[safeIdx];
    const url = typeof photo === 'string' ? photo : photo?.url;
    const caption = typeof photo === 'string' ? '' : photo?.caption || '';
    setLightbox({ isOpen: true, index: safeIdx, url, caption });
  }, [job]);

  const openLightbox = (photo, index) => {
    const url = typeof photo === 'string' ? photo : photo?.url;
    const caption = typeof photo === 'string' ? '' : photo?.caption || '';
    if (url) {
      setLightbox({ isOpen: true, index, url, caption });
      setLbScale(1);
      setLbPan({ x: 0, y: 0 });
    }
  };

  const nextPhoto = () => goToPhoto(lightbox.index + 1);
  const prevPhoto = () => goToPhoto(lightbox.index - 1);
  const closeLightbox = () => {
    setLightbox({ isOpen: false, index: 0, url: '', caption: '' });
    setLbScale(1);
    setLbPan({ x: 0, y: 0 });
  };

  const lbZoomIn = (e) => { e?.stopPropagation(); setLbScale(s => Math.min(s + 0.5, 5)); };
  const lbZoomOut = (e) => {
    e?.stopPropagation();
    setLbScale(s => {
      const ns = Math.max(s - 0.5, 0.5);
      if (ns <= 1) setLbPan({ x: 0, y: 0 });
      return ns;
    });
  };
  const lbWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) setLbScale(s => Math.min(s + 0.25, 5));
    else setLbScale(s => { const ns = Math.max(s - 0.25, 0.5); if (ns <= 1) setLbPan({ x: 0, y: 0 }); return ns; });
  };
  const lbMouseDown = (e) => {
    if (lbScale > 1) { setLbDragging(true); setLbDragStart({ x: e.clientX - lbPan.x, y: e.clientY - lbPan.y }); }
  };
  const lbMouseMove = (e) => { if (lbDragging && lbScale > 1) setLbPan({ x: e.clientX - lbDragStart.x, y: e.clientY - lbDragStart.y }); };
  const lbMouseUp = () => setLbDragging(false);
  const lbDownload = (e) => {
    e?.stopPropagation();
    if (lightbox.url) {
      const link = document.createElement('a');
      link.href = lightbox.url;
      link.download = lightbox.caption || 'job-photo';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // ─── Download All Job Photos (client-side JSZip) ───
  const handleDownloadAllPhotos = async () => {
    if (!job?.jobPhotos?.length) return;
    const urls = job.jobPhotos
      .map(p => typeof p === 'string' ? p : p?.url)
      .filter(Boolean);
    if (!urls.length) return;

    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();
    const folder = zip.folder(job.title || 'job-photos');

    let completed = 0;
    for (const url of urls) {
      try {
        const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
        if (!response.ok) throw new Error('Fetch failed');
        const blob = await response.blob();
        const urlObj = new URL(url);
        const fileName = urlObj.pathname.split('/').pop() || `photo-${completed}.jpg`;
        folder.file(decodeURIComponent(fileName), blob);
        completed++;
      } catch (err) {
        console.warn('[DownloadAll] Failed to fetch:', url, err.message);
      }
    }

    if (completed === 0) {
      alert('No images could be downloaded. Please try again.');
      return;
    }

    const content = await zip.generateAsync({ type: 'blob' });
    const blobUrl = URL.createObjectURL(content);
    const link = document.createElement('a');
    link.href = blobUrl;
    const jobDate = job.scheduledDate ? new Date(job.scheduledDate).toLocaleDateString('en-GB').replace(/\//g, '-') : '';
    const safeTitle = (job.title || 'job-photos').replace(/[^a-zA-Z0-9\s-]/g, '').trim();
    link.download = jobDate ? `${safeTitle} - ${jobDate}.zip` : `${safeTitle}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  };

  useEffect(() => {
    if (!lightbox.isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowRight') nextPhoto();
      if (e.key === 'ArrowLeft') prevPhoto();
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightbox.isOpen, lightbox.index]);

  const [touchStart, setTouchStart] = useState(null);
  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (diff > 50) nextPhoto();
    if (diff < -50) prevPhoto();
    setTouchStart(null);
  };

  // ─── Zoho Resync Functions ───
  const statusColors = {
    scheduled: '#f59e0b',
    'in-progress': '#3b82f6',
    completed: '#10b981',
    cancelled: '#ef4444',
  };

  const handleZohoSearch = async () => {
    if (!zohoSearch.trim()) return;
    const connected = await isZohoConnected();
    if (!connected) { setZohoConnected(false); return; }
    setZohoConnected(true);
    setZohoLoading(true);
    const { customers, error } = await searchZohoCustomers(zohoSearch);
    setZohoCustomers(customers);
    setShowZohoResults(true);
    setZohoLoading(false);
    if (error) console.error('Zoho search error:', error);
  };

  const handleConnectZoho = async () => {
    const { success, error } = await connectZohoAccount();
    if (success) {
      setZohoConnected(true);
      if (zohoSearch.trim()) handleZohoSearch();
    } else {
      console.error('Zoho connection failed:', error);
    }
  };

  const handleSelectZohoCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setEstimatesLoading(true);
    setInvoicesLoading(true);
    
    // Load both estimates and invoices in parallel
    const [{ estimates }, { invoices }] = await Promise.all([
      getZohoEstimates(customer.contact_id),
      getZohoInvoices(customer.contact_id)
    ]);
    
    setZohoEstimates(estimates);
    setZohoInvoices(invoices);
    setEstimatesLoading(false);
    setInvoicesLoading(false);
    
    setEditForm(prev => ({
      ...prev,
      zohoCustomerId: customer.contact_id || '',
      companyName: customer.contact_name || customer.company_name || '',
      clientContact: customer.contact_name || '',
      clientEmail: customer.email || '',
      clientPhone: customer.phone || customer.mobile || '',
      primarySiteContact: customer.billing_address?.attention || '',
      companyAddress: [
        customer.billing_address?.street1,
        customer.billing_address?.street2,
        customer.billing_address?.city,
        customer.billing_address?.state,
        customer.billing_address?.zip,
        customer.billing_address?.country,
      ].filter(Boolean).join(', ') || '',
      title: customer.contact_name || prev.title,
      zohoSynced: true,
      zohoSyncDate: new Date().toISOString(),
    }));
    
    // Show the documents panel
    setShowEstimates(estimates.length > 0);
    setShowInvoices(invoices.length > 0);
    if (estimates.length === 0 && invoices.length === 0) {
      setShowZohoResults(false);
      setZohoSearch('');
    }
  };

  const handleSelectEstimate = async (estimate) => {
    const { estimate: details, error } = await getZohoEstimateDetails(estimate.estimate_id);
    if (details && selectedCustomer) {
      const jobData = transformZohoToJob(selectedCustomer, details, 'estimate');
      setEditForm(prev => ({
        ...prev,
        ...jobData,
        title: jobData.title || prev.title,
        zohoEstimateId: estimate.estimate_id || '',
        zohoInvoiceId: '',
        zohoSynced: true,
        zohoSyncDate: new Date().toISOString(),
      }));
    }
    setShowEstimates(false);
    setShowInvoices(false);
    setShowZohoResults(false);
    setZohoSearch('');
  };

  const handleSelectInvoice = async (invoice) => {
    const { invoice: details, error } = await getZohoInvoiceDetails(invoice.invoice_id);
    if (details && selectedCustomer) {
      const jobData = transformZohoToJob(selectedCustomer, details, 'invoice');
      setEditForm(prev => ({
        ...prev,
        ...jobData,
        title: jobData.title || prev.title,
        zohoInvoiceId: invoice.invoice_id || '',
        zohoEstimateId: '',
        zohoSynced: true,
        zohoSyncDate: new Date().toISOString(),
      }));
    }
    setShowEstimates(false);
    setShowInvoices(false);
    setShowZohoResults(false);
    setZohoSearch('');
  };

  const handleUnsyncZoho = () => {
    setEditForm(prev => ({
      ...prev,
      zohoSynced: false,
      zohoCustomerId: '',
      zohoEstimateId: '',
      zohoInvoiceId: '',
      zohoSyncDate: null,
    }));
  };

  // ─── Multiple Workers Helpers ───
  const getEditWorkers = () => {
    const arr = Array.isArray(editForm.assignedToName) 
      ? editForm.assignedToName.map((name, i) => ({ 
          uid: Array.isArray(editForm.assignedTo) ? editForm.assignedTo[i] : '', 
          displayName: name 
        }))
      : editForm.assignedToName 
        ? [{ uid: editForm.assignedTo || '', displayName: editForm.assignedToName }]
        : [];
    return arr;
  };

  const addEditWorker = () => {
    const current = getEditWorkers();
    setEditForm(prev => ({
      ...prev,
      assignedTo: [...(Array.isArray(prev.assignedTo) ? prev.assignedTo : prev.assignedTo ? [prev.assignedTo] : []), ''],
      assignedToName: [...(Array.isArray(prev.assignedToName) ? prev.assignedToName : prev.assignedToName ? [prev.assignedToName] : []), ''],
    }));
  };

  const removeEditWorker = (index) => {
    const currentTo = Array.isArray(editForm.assignedTo) ? [...editForm.assignedTo] : editForm.assignedTo ? [editForm.assignedTo] : [];
    const currentNames = Array.isArray(editForm.assignedToName) ? [...editForm.assignedToName] : editForm.assignedToName ? [editForm.assignedToName] : [];
    currentTo.splice(index, 1);
    currentNames.splice(index, 1);
    setEditForm(prev => ({
      ...prev,
      assignedTo: currentTo,
      assignedToName: currentNames,
    }));
  };

  const updateEditWorker = (index, uid) => {
    const w = workers.find(x => x.uid === uid);
    const currentTo = Array.isArray(editForm.assignedTo) ? [...editForm.assignedTo] : editForm.assignedTo ? [editForm.assignedTo] : [];
    const currentNames = Array.isArray(editForm.assignedToName) ? [...editForm.assignedToName] : editForm.assignedToName ? [editForm.assignedToName] : [];
    currentTo[index] = w?.uid || '';
    currentNames[index] = w?.displayName || '';
    setEditForm(prev => ({
      ...prev,
      assignedTo: currentTo,
      assignedToName: currentNames,
    }));
  };

  if (loading) return <div className="page"><p>Loading...</p></div>;
  if (!job) return <div className="page"><p>Job not found.</p></div>;

  const canEditTimesheet = isAdmin || (Array.isArray(job.assignedTo) ? job.assignedTo.includes(user?.uid) : job.assignedTo === user?.uid);
  const isMultiDay = job.isMultiDay || job.scheduledEndDate;
  
  // Handle both old single-worker and new multi-worker data
  const jobWorkers = Array.isArray(job.assignedToName)
    ? job.assignedToName.filter(Boolean)
    : job.assignedToName ? [job.assignedToName] : [];

  // Zip worker UIDs with names for clickable links
  const jobWorkersWithUids = Array.isArray(job.assignedTo)
    ? job.assignedTo.map((uid, i) => ({
        uid,
        name: job.assignedToName?.[i] || 'Unknown',
      })).filter(w => w.uid)
    : job.assignedTo
    ? [{ uid: job.assignedTo, name: job.assignedToName || 'Unknown' }]
    : [];

  console.log('[JobDetail] Rendering job:', id, 'status:', job.status, 'completedAt:', job.completedAt);

  const backPath = location.state?.returnTo || '/jobs';

  return (
    <div className="page job-detail">
      <button className="back-btn no-print" onClick={() => navigate(backPath)}>
        ← Back
      </button>

      <div className="jd-header">
        <h2>{job.title}</h2>
        <span
          className="jd-status no-print"
          style={{
            backgroundColor: statusColors[job.status] || '#6b7280',
            textTransform: 'capitalize',
          }}
        >
          {job.status || 'Unknown'}
        </span>
      </div>

      {/* ─── Completed Timestamp ─── */}
      {job.status === 'completed' && job.completedAt && (
        <div className="jd-completed-info no-print">
          ✅ Completed on {new Date(job.completedAt).toLocaleString('en-GB', {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}
        </div>
      )}

      {/* ─── Zoho Sync Badge ─── */}
      {job.zohoSynced && (
        <div className="jd-zoho-badge no-print">
          🔗 Synced from Zoho Books {job.zohoSyncDate && `• ${new Date(job.zohoSyncDate).toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' })}`}
        </div>
      )}

      {/* ─── Multi-Day Badge ─── */}
      {isMultiDay && (
        <div className="jd-multiday-badge no-print">
          📅 Multi-Day: {job.scheduledDate ? new Date(job.scheduledDate).toLocaleDateString('en-GB') : ''} → {job.scheduledEndDate ? new Date(job.scheduledEndDate).toLocaleDateString('en-GB') : ''}
        </div>
      )}

      {/* ─── Close Job Confirmation Prompt ─── */}
      {showClosePrompt && job.status !== 'completed' && (
        <div className="jd-modal-overlay no-print">
          <div className="jd-modal">
            <h3>🎉 Timesheet Submitted!</h3>
            <p>Your timesheet has been saved. Would you like to mark this job as <strong>completed</strong>?</p>
            <div className="jd-modal-actions">
              <button className="btn-primary" onClick={handleCloseJob}>
                ✅ Yes, Close Job
              </button>
              <button className="btn-secondary" onClick={handleKeepJobOpen}>
                🔁 Keep Open
              </button>
            </div>
          </div>
        </div>
      )}

      {editing ? (
        <div className="jd-edit">
          {/* ─── Zoho Resync Section — Admin Only ─── */}
          {isAdmin && (
          <div className="zoho-resync-section">
            <h4>🔗 Zoho Books Integration</h4>
            {editForm.zohoSynced && editForm.zohoEstimateId && (
              <p className="zoho-current-estimate">
                Current estimate: <strong>{editForm.zohoEstimateId}</strong>
              </p>
            )}
            <div>
              <button className="zoho-resync-btn" onClick={handleConnectZoho}>
                🔗 Connect / Refresh Zoho
              </button>
              <button className="zoho-resync-btn" onClick={() => { setZohoSearch(''); setShowZohoResults(true); }}>
                🔍 Search Customer
              </button>
              {editForm.zohoSynced && (
                <button className="zoho-unsync-btn" onClick={handleUnsyncZoho}>
                  ❌ Unsync from Zoho
                </button>
              )}
            </div>
            
            {showZohoResults && (
              <div style={{ marginTop: '12px' }}>
                <div className="zoho-search-box">
                  <input
                    type="text"
                    placeholder="Search customer..."
                    value={zohoSearch}
                    onChange={(e) => setZohoSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleZohoSearch()}
                  />
                  <button className="btn btn-primary" onClick={handleZohoSearch} disabled={zohoLoading}>
                    {zohoLoading ? 'Searching...' : 'Search'}
                  </button>
                </div>
                {zohoCustomers.length > 0 && (
                  <div className="zoho-results">
                    <p className="zoho-results-title">Select a customer:</p>
                    {zohoCustomers.map((c) => (
                      <div key={c.contact_id} className="zoho-result-item" onClick={() => handleSelectZohoCustomer(c)}>
                        <strong>{c.contact_name}</strong><br />
                        <small>{c.email && <span>{c.email}</span>}{c.email && c.phone && <span> · </span>}{c.phone && <span>{c.phone}</span>}</small>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* ─── Estimates & Invoices Tabs ─── */}
                {(zohoEstimates.length > 0 || zohoInvoices.length > 0) && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                      {zohoEstimates.length > 0 && (
                        <button 
                          className="zoho-resync-btn" 
                          style={{ 
                            background: zohoDocType === 'estimate' ? '#dbeafe' : '#f3f4f6',
                            color: zohoDocType === 'estimate' ? '#1e40af' : '#6b7280'
                          }}
                          onClick={() => setZohoDocType('estimate')}
                        >
                          📋 Estimates ({zohoEstimates.length})
                        </button>
                      )}
                      {zohoInvoices.length > 0 && (
                        <button 
                          className="zoho-resync-btn" 
                          style={{ 
                            background: zohoDocType === 'invoice' ? '#dbeafe' : '#f3f4f6',
                            color: zohoDocType === 'invoice' ? '#1e40af' : '#6b7280'
                          }}
                          onClick={() => setZohoDocType('invoice')}
                        >
                          🧾 Invoices ({zohoInvoices.length})
                        </button>
                      )}
                    </div>
                    
                    {zohoDocType === 'estimate' && zohoEstimates.length > 0 && (
                      <div className="zoho-estimates">
                        <p className="zoho-estimates-title">Select an estimate:</p>
                        {zohoEstimates.map((est) => (
                          <div key={est.estimate_id} className="zoho-estimate-item" onClick={() => handleSelectEstimate(est)}>
                            <strong>{est.estimate_number || 'No #'}</strong><br />
                            <small>{est.date && <span>{est.date}</span>}{est.date && est.total && <span> · </span>}{est.total && <span>£{est.total}</span>}</small>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {zohoDocType === 'invoice' && zohoInvoices.length > 0 && (
                      <div className="zoho-estimates">
                        <p className="zoho-estimates-title">Select an invoice:</p>
                        {zohoInvoices.map((inv) => (
                          <div key={inv.invoice_id} className="zoho-estimate-item" onClick={() => handleSelectInvoice(inv)}>
                            <strong>{inv.invoice_number || 'No #'}</strong><br />
                            <small>{inv.date && <span>{inv.date}</span>}{inv.date && inv.total && <span> · </span>}{inv.total && <span>£{inv.total}</span>}</small>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {estimatesLoading && invoicesLoading && <p className="zoho-loading">Loading documents...</p>}
              </div>
            )}
          </div>
          )}

          {/* ─── Basic Info ─── */}
          <h4>Basic Info</h4>
          <input value={editForm.title || ''} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} placeholder="Job Title" />
          <input value={editForm.location || ''} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} placeholder="Location" />
          <input type="datetime-local" value={editForm.scheduledDate?.slice(0, 16)} onChange={(e) => setEditForm({ ...editForm, scheduledDate: e.target.value })} />

          {/* ─── Multi-Day Toggle (Edit Mode) ─── */}
          <label className="checkbox-label" style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={editForm.isMultiDay || false}
              onChange={(e) => setEditForm({ ...editForm, isMultiDay: e.target.checked })}
              style={{ width: 'auto', margin: 0 }}
            />
            Multi-Day Job (consecutive days)
          </label>
          {(editForm.isMultiDay || editForm.scheduledEndDate) && (
            <input
              type="date"
              value={editForm.scheduledEndDate ? (editForm.scheduledEndDate.includes('T') ? editForm.scheduledEndDate.split('T')[0] : editForm.scheduledEndDate) : ''}
              min={editForm.scheduledDate ? editForm.scheduledDate.split('T')[0] : ''}
              onChange={(e) => setEditForm({ ...editForm, scheduledEndDate: e.target.value })}
              placeholder="End Date"
            />
          )}
          <textarea rows={2} value={editForm.description || ''} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} placeholder="Description" />

          {/* ─── Company / Client ─── */}
          <h4>Company & Client</h4>
          <input value={editForm.companyName || ''} onChange={(e) => setEditForm({ ...editForm, companyName: e.target.value })} placeholder="Company Name" />
          <input value={editForm.companyAddress || ''} onChange={(e) => setEditForm({ ...editForm, companyAddress: e.target.value })} placeholder="Company Address" />
          <input value={editForm.clientContact || ''} onChange={(e) => setEditForm({ ...editForm, clientContact: e.target.value })} placeholder="Client Contact" />
          <input value={editForm.clientEmail || ''} onChange={(e) => setEditForm({ ...editForm, clientEmail: e.target.value })} placeholder="Client Email" />
          <input value={editForm.clientPhone || ''} onChange={(e) => setEditForm({ ...editForm, clientPhone: e.target.value })} placeholder="Client Phone" />

          {/* ─── Site Contact ─── */}
          <h4>Site Contact</h4>
          <input value={editForm.primarySiteContact || ''} onChange={(e) => setEditForm({ ...editForm, primarySiteContact: e.target.value })} placeholder="Primary Site Contact" />
          <input value={editForm.siteContactPhone || ''} onChange={(e) => setEditForm({ ...editForm, siteContactPhone: e.target.value })} placeholder="Site Contact Phone" />

          {/* ─── Job Details ─── */}
          <h4>Job Details</h4>
          <input value={editForm.jobType || ''} onChange={(e) => setEditForm({ ...editForm, jobType: e.target.value })} placeholder="Job Type" />
          <input value={editForm.jobReference || ''} onChange={(e) => setEditForm({ ...editForm, jobReference: e.target.value })} placeholder="Job Reference #" />
          <textarea rows={4} value={editForm.jobDetails || ''} onChange={(e) => setEditForm({ ...editForm, jobDetails: e.target.value })} placeholder="Detailed Job Description" />
          <textarea rows={3} value={editForm.additionalInfo || ''} onChange={(e) => setEditForm({ ...editForm, additionalInfo: e.target.value })} placeholder="Additional Information" />

          {/* ─── Status (Admin Only) ─── */}
          {isAdmin && (
            <>
              <h4>Status</h4>
              <select
                value={editForm.status || 'scheduled'}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
              >
                <option value="scheduled">Scheduled</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </>
          )}

          {/* ─── Multiple Workers ─── */}
          {isAdmin && (
            <>
              <h4>Assign Workers</h4>
              {getEditWorkers().map((w, index) => (
                <div key={index} className="worker-select-row">
                  <select value={w.uid} onChange={(e) => updateEditWorker(index, e.target.value)}>
                    <option value="">-- Select worker --</option>
                    {workers.map((worker) => (
                      <option key={worker.uid} value={worker.uid}>{worker.label || worker.displayName}</option>
                    ))}
                  </select>
                  <button type="button" className="worker-remove-btn" onClick={() => removeEditWorker(index)} title="Remove worker">×</button>
                </div>
              ))}
              <button type="button" className="worker-add-btn" onClick={addEditWorker}>+ Add another worker</button>
              {getEditWorkers().filter(w => w.uid).length > 0 && (
                <div className="worker-chips">
                  {getEditWorkers().filter(w => w.uid).map((w, i) => (
                    <span key={i} className="worker-chip">{w.displayName}</span>
                  ))}
                </div>
              )}
            </>
          )}
          
          <div className="jd-actions">
            <button className="btn-primary" onClick={handleUpdate}>💾 Save</button>
            <button className="btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="jd-info">
          {/* ─── Location & Date ─── */}
          <p className="jd-meta">
            📍{' '}
            <Link
              to={`/jobs/location/${encodeURIComponent(job.location || '')}`}
              className="jd-location-link"
              onClick={(e) => e.stopPropagation()}
            >
              {job.location}
            </Link>
          </p>
          <p className="jd-meta">
            📅 {job.scheduledDate ? new Date(job.scheduledDate).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : 'No date'}
            {isMultiDay && job.scheduledEndDate && (
              <span> → {new Date(job.scheduledEndDate).toLocaleDateString('en-GB')}</span>
            )}
          </p>
          
          {/* ─── Multiple Workers Display ─── */}
          {jobWorkersWithUids.length > 0 && (
            <div className="jd-workers">
              {jobWorkersWithUids.map((w, i) => (
                <span key={i} className="jd-worker-chip">
                  👷{' '}
                  <Link
                    to={`/jobs/worker/${w.uid}?name=${encodeURIComponent(w.name)}`}
                    className="jd-worker-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {w.name}
                  </Link>
                </span>
              ))}
            </div>
          )}

          {/* ─── Company Card ─── */}
          {job.companyName && (
            <div className="jd-company-card">
              <h4>🏢 {job.companyName}</h4>
              {job.companyAddress && <p>{job.companyAddress}</p>}
              {job.clientContact && (
                <div className="jd-contact-row">
                  <span>👤 {job.clientContact}</span>
                  {job.clientPhone && <span>📞 {job.clientPhone}</span>}
                </div>
              )}
              {job.clientEmail && <p>✉️ {job.clientEmail}</p>}
            </div>
          )}

          {/* ─── Site Contact ─── */}
          {job.primarySiteContact && (
            <div className="jd-contact-card">
              <h4>👷 Site Contact</h4>
              <p>{job.primarySiteContact}</p>
              {job.siteContactPhone && <p>📞 {job.siteContactPhone}</p>}
            </div>
          )}

          {/* ─── Job Details ─── */}
          <div className="jd-details-card">
            {job.jobType && (
              <p><strong>🔧 Job Type:</strong> {job.jobType}</p>
            )}
            {job.jobReference && (
              <p><strong>📋 Reference:</strong> {job.jobReference}</p>
            )}
            {job.jobDetails && (
              <div className="jd-detail-block">
                <strong>📄 Job Details</strong>
                <p>{job.jobDetails}</p>
              </div>
            )}
            {job.additionalInfo && (
              <div className="jd-detail-block">
                <strong>ℹ️ Additional Information</strong>
                <p>{job.additionalInfo}</p>
              </div>
            )}
            {job.description && !job.jobDetails && (
              <p className="jd-desc">{job.description}</p>
            )}
          </div>

          {isAdmin && (
            <button className="btn-secondary no-print" onClick={startEdit}>
              ✏️ Edit Job
            </button>
          )}
          {isAdmin && (
            <button className="btn-secondary no-print" onClick={handleCloneJob} style={{ marginLeft: '8px' }}>
              📋 Clone Job
            </button>
          )}
        </div>
      )}

      {/* ─── SIGNED TIMESHEET (Mandatory) ─── */}
      <section className="jd-section signed-timesheet-section">
        <div className="section-header-row">
          <h3>📋 Signed Timesheet</h3>
          <span className="mandatory-badge">Mandatory</span>
        </div>
        <p className="section-desc">
          Get the client to sign and save this timesheet before completing the job.
        </p>
        
        {jobSignedTs && (
          <div className="signed-ts-exists no-print">
            <span className="signed-ts-status">✓ Signed timesheet saved</span>
            <button className="ts-view-btn" onClick={() => setShowSignedModal(true)}>
              View Timesheet
            </button>
          </div>
        )}
        
        <TimesheetPDF job={job} timesheets={timesheets} existingSignedTs={jobSignedTs} />
      </section>

      {/* ─── CLOCK IN / CLOCK OUT (Optional) ─── */}
      <section className="jd-section clock-timesheet-section">
        <div className="section-header-row">
          <h3>⏱️ Clock In / Clock Out</h3>
        </div>
        {canEditTimesheet && job.status !== 'completed' && (
          <div className="ts-form-wrapper no-print">
            <TimesheetForm jobId={id} onSubmit={addTimesheet} onComplete={handleTimesheetSubmitted} />
          </div>
        )}
        {job.status === 'completed' && (
          <div className="jd-completed-banner">
            🔒 This job is completed. No more clock-in entries can be added.
          </div>
        )}
        {timesheets.length === 0 ? (
          <p className="empty">No clock-in entries yet.</p>
        ) : (
          <ul className="ts-list">
            {timesheets.map((ts) => (
              <li key={ts.id} className="ts-item">
                <p>
                  <strong>{ts.workerName || 'Worker'}</strong> — {ts.hours} hrs
                </p>
                <p className="ts-times">
                  {new Date(ts.clockIn).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })} · {new Date(ts.clockIn).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })} → {new Date(ts.clockOut).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })}
                </p>
                {ts.notes && <p className="ts-notes">{ts.notes}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ─── Files & Photos (consolidated) — moved to bottom for performance ─── */}
      <section className="jd-section no-print">
        <PhotoUpload jobId={id} onPhotosSaved={handlePhotosSaved} legacyPhotos={job.jobPhotos} jobTitle={job?.title} jobDate={job?.scheduledDate} />
      </section>

      {/* ─── View Saved Signed Timesheet Modal ─── */}
      {showSignedModal && jobSignedTs && (
        <div className="modal-overlay" onClick={() => setShowSignedModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowSignedModal(false)}>✕</button>
            <h3>Signed Timesheet</h3>
            <div className="modal-meta">
              <p><strong>Job:</strong> {jobSignedTs.jobTitle}</p>
              <p><strong>Client:</strong> {jobSignedTs.clientName}</p>
              <p><strong>Technician:</strong> {jobSignedTs.technician}</p>
              <p><strong>Signed by:</strong> {jobSignedTs.signerName} on {jobSignedTs.signerDate}</p>
            </div>
            {jobSignedTs.signature ? (
              <div className="modal-signature-section">
                <p><strong>Signature:</strong></p>
                <img src={jobSignedTs.signature} alt="Client signature" className="modal-sig-large" />
              </div>
            ) : (
              <p className="modal-no-sig">No signature on file</p>
            )}
            {isAdmin && (
              <div className="modal-actions" style={{ marginTop: '16px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button
                  className="btn-danger"
                  onClick={async () => {
                    if (!window.confirm('Delete this signed timesheet? The signature will need to be retaken.')) return;
                    try {
                      await deleteSignedTimesheet(jobSignedTs.id);
                      setJobSignedTs(null);
                      setShowSignedModal(false);
                    } catch (err) {
                      console.error('Failed to delete signed timesheet:', err);
                      alert('Failed to delete timesheet.');
                    }
                  }}
                >
                  🗑️ Delete Timesheet
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetail;
